import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { X, Send } from "lucide-react";

export const SendMessageModal = ({ members, childLodges, canPickLodge, onRepliesSeen, onClose }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("compose");
  const [target, setTarget] = useState("all");
  const [childId, setChildId] = useState("");
  const [selected, setSelected] = useState({});
  const [q, setQ] = useState("");
  const [text, setText] = useState("");
  const [translate, setTranslate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (tab === "sent" && sent === null) {
      api.get("/messages").then(({ data }) => setSent(data)).catch(() => setSent([]));
      api.post("/messages/replies/seen").then(() => onRepliesSeen?.()).catch(() => {});
    }
  }, [tab, sent, onRepliesSeen]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return members.filter((m) => !s || `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase().includes(s));
  }, [members, q]);

  const selCount = Object.values(selected).filter(Boolean).length;
  const canSend = text.trim() && !busy &&
    (target !== "child" || childId) &&
    (target !== "members" || selCount > 0);

  const doSend = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const payload = { body: text, translate, target };
      if (target === "child") payload.child_lodge_id = childId;
      if (target === "members") payload.member_ids = Object.keys(selected).filter((k) => selected[k]);
      const { data } = await api.post("/messages/send", payload);
      setOk(t("members.msgSentOk", { count: data.recipients }));
      setText(""); setSelected({}); setSent(null);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const tabBtn = (key, label, tid) => (
    <button type="button" onClick={() => setTab(key)} data-testid={tid}
      style={{
        padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: "0.75rem",
        letterSpacing: "0.06em", border: "1px solid #C8A96A",
        background: tab === key ? "#0d1b2a" : "transparent", color: tab === key ? "#e8c97a" : "#0d1b2a",
      }}>{label}</button>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content gold-card" onClick={(e) => e.stopPropagation()} data-testid="send-message-modal"
        style={{ maxWidth: 580, padding: "1.4rem", maxHeight: "85vh", overflowY: "auto" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 style={{ fontFamily: "Georgia, serif", fontSize: "1.05rem", margin: 0 }}>{t("members.msgTitle", "Mesaj către frați")}</h3>
          <button onClick={onClose} className="text-gray-500" data-testid="send-message-close"><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-4">
          {tabBtn("compose", t("members.msgTabCompose", "Mesaj nou"), "msg-tab-compose")}
          {tabBtn("sent", t("members.msgTabSent", "Trimise & răspunsuri"), "msg-tab-sent")}
        </div>

        {tab === "compose" ? (
          <div>
            <select className="input-lodge" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="msg-target-select">
              <option value="all">{t("members.msgTargetAll", "Toți frații")}</option>
              {canPickLodge && <option value="child">{t("members.msgTargetChild", "O lojă anume")}</option>}
              <option value="members">{t("members.msgTargetSelected", "Frați selectați")}</option>
            </select>
            {target === "child" && (
              <select className="input-lodge" style={{ marginTop: 8 }} value={childId} onChange={(e) => setChildId(e.target.value)} data-testid="msg-child-select">
                <option value="">—</option>
                {childLodges.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {target === "members" && (
              <div style={{ marginTop: 8 }}>
                <input className="input-lodge" placeholder={t("members.msgSearchPh", "Caută frați...")} value={q}
                  onChange={(e) => setQ(e.target.value)} data-testid="msg-member-search" />
                <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--cream-200)", borderRadius: 8, marginTop: 6, padding: "4px 8px" }} data-testid="msg-member-list">
                  {filtered.map((m) => (
                    <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", cursor: "pointer", fontSize: "0.85rem" }}>
                      <input type="checkbox" checked={!!selected[m.id]}
                        onChange={(e) => setSelected((p) => ({ ...p, [m.id]: e.target.checked }))}
                        data-testid={`msg-member-check-${m.id}`} />
                      {m.first_name} {m.last_name}
                    </label>
                  ))}
                </div>
                <div className="kv-label" style={{ marginTop: 4 }}>{t("members.msgRecipients", { count: selCount })}</div>
              </div>
            )}
            <textarea className="input-lodge" rows={5} style={{ marginTop: 10, resize: "vertical" }}
              placeholder={t("members.msgPlaceholder", "Scrie mesajul...")} value={text} maxLength={2000}
              onChange={(e) => setText(e.target.value)} data-testid="msg-body-input" />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: "0.82rem", cursor: "pointer" }}>
              <input type="checkbox" checked={translate} onChange={(e) => setTranslate(e.target.checked)} data-testid="msg-translate-toggle" />
              {t("members.msgTranslateAuto", "Tradu automat în limba fiecărui frate")}
            </label>
            {err && <div className="error-banner mt-3" data-testid="msg-error">{err}</div>}
            {ok && <div style={{ color: "#3e6a3c", fontWeight: 700, fontSize: "0.85rem", marginTop: 10 }} data-testid="msg-success">{ok}</div>}
            <button className="btn-primary-gold flex items-center gap-2" style={{ marginTop: 12 }}
              onClick={doSend} disabled={!canSend} data-testid="msg-send-btn">
              <Send size={14} /> {t("members.msgSend", "Trimite")}
            </button>
          </div>
        ) : (
          <div data-testid="msg-sent-list">
            {sent === null && <p className="kv-label">...</p>}
            {sent && sent.length === 0 && <p style={{ fontStyle: "italic", color: "#858481" }}>{t("members.msgNoMessages", "Niciun mesaj trimis încă.")}</p>}
            {(sent || []).map((m) => (
              <div key={m.id} style={{ border: "1px solid var(--cream-200)", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }} data-testid={`msg-sent-${m.id}`}>
                <div className="kv-label" style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{new Date(m.created_at).toLocaleString()}</span>
                  <span>{t("members.msgRecipients", { count: m.recipient_count })}</span>
                </div>
                <div style={{ fontSize: "0.9rem", marginTop: 4, whiteSpace: "pre-wrap" }}>{m.body}</div>
                <div className="kv-label" style={{ marginTop: 8 }}>
                  {t("members.msgReplies", "Răspunsuri")}: {m.replies.length === 0 && <em>{t("members.msgNoReplies", "Fără răspunsuri încă")}</em>}
                </div>
                {m.replies.map((r) => (
                  <div key={r.id} style={{ background: "rgba(200,169,106,0.12)", borderRadius: 8, padding: "6px 10px", marginTop: 6, fontSize: "0.84rem" }} data-testid={`msg-reply-${r.id}`}>
                    <strong>{r.member_name}</strong> · <span className="kv-label">{new Date(r.created_at).toLocaleString()}</span>
                    <div style={{ marginTop: 2, whiteSpace: "pre-wrap" }}>{r.body}</div>
                    {r.body_original && (
                      <div style={{ marginTop: 3, fontStyle: "italic", fontSize: "0.76rem", color: "#858481" }}
                        data-testid={`msg-reply-original-${r.id}`}>
                        {t("members.msgReplyOriginal", "Original")}: {r.body_original}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SendMessageModal;
